/**
 * Sixy theme — paletas por mood e tokens visuais compartilhados.
 *
 * Centraliza cores e timings da Sixy num único arquivo. Componentes que
 * renderizam Sixy (Orb, Robot face, Concierge, etc.) importam daqui em vez
 * de hardcode-ar valores.
 *
 * Paletas premium dark mode:
 *  - Sempre 4 stops de cor: core (claro/brilhante) → mid1 → mid2 → edge (escura/translúcida)
 *  - Particle: cor das partículas orbitando
 *  - Accent: cor de UI (text, border, glow) que combina com o mood
 *
 * Timings:
 *  - Respiração varia por mood (excited mais rápida, sad mais lenta)
 *  - Scale do pulse igualmente varia
 *  - Particle rotation speed também
 */

export type SixyMood =
  | 'happy'
  | 'thinking'
  | 'sad'
  | 'confused'
  | 'excited'
  | 'error';

export type SixySize = 'sm' | 'md' | 'lg' | 'xl';

export interface SixyPalette {
  /** Centro brilhante do orb. */
  core: string;
  /** Camada média 1 — cor de identidade principal. */
  mid1: string;
  /** Camada média 2 — cor de transição. */
  mid2: string;
  /** Borda translúcida do orb. */
  edge: string;
  /** Cor das partículas orbitando. */
  particle: string;
  /** Cor de UI (text/border/glow accent). */
  accent: string;
  /** Glow rgba (sem o "rgba(" prefix) — pra interpolação com opacidade. */
  glowRgb: string;
}

export const SIXY_PALETTES: Record<SixyMood, SixyPalette> = {
  happy: {
    core: '#fef3c7',
    mid1: '#ec4899',
    mid2: '#a855f7',
    edge: '#22d3ee',
    particle: '#fbbf24',
    accent: '#a855f7',
    glowRgb: '168, 85, 247',
  },
  thinking: {
    core: '#e0e7ff',
    mid1: '#a855f7',
    mid2: '#8b5cf6',
    edge: '#6366f1',
    particle: '#c084fc',
    accent: '#a855f7',
    glowRgb: '139, 92, 246',
  },
  sad: {
    core: '#e0e7ff',
    mid1: '#818cf8',
    mid2: '#6366f1',
    edge: '#3730a3',
    particle: '#a5b4fc',
    accent: '#818cf8',
    glowRgb: '99, 102, 241',
  },
  confused: {
    core: '#fef3c7',
    mid1: '#fbbf24',
    mid2: '#a855f7',
    edge: '#22d3ee',
    particle: '#fde047',
    accent: '#fbbf24',
    glowRgb: '251, 191, 36',
  },
  excited: {
    core: '#fff1f2',
    mid1: '#fb7185',
    mid2: '#ec4899',
    edge: '#a855f7',
    particle: '#fda4af',
    accent: '#ec4899',
    glowRgb: '251, 113, 133',
  },
  error: {
    core: '#fef3c7',
    mid1: '#fbbf24',
    mid2: '#f43f5e',
    edge: '#7f1d1d',
    particle: '#fde047',
    accent: '#f43f5e',
    glowRgb: '244, 63, 94',
  },
};

export const SIXY_SIZE_PX: Record<SixySize, number> = {
  sm: 40,
  md: 60,
  lg: 96,
  xl: 140,
};

export interface MoodTiming {
  /** Duração de 1 ciclo de respiração (s). */
  pulseDurationSec: number;
  /** Scale máximo no pulse (1.045 = 4.5% de expansão). */
  pulseScale: number;
  /** Velocidade base de rotação das partículas (s para 1 volta). */
  particleSpeedSec: number;
}

export const SIXY_TIMINGS: Record<SixyMood, MoodTiming> = {
  happy: { pulseDurationSec: 3.2, pulseScale: 1.045, particleSpeedSec: 10 },
  thinking: { pulseDurationSec: 3.0, pulseScale: 1.045, particleSpeedSec: 6 },
  sad: { pulseDurationSec: 4.2, pulseScale: 1.03, particleSpeedSec: 18 },
  confused: { pulseDurationSec: 2.8, pulseScale: 1.05, particleSpeedSec: 8 },
  excited: { pulseDurationSec: 1.8, pulseScale: 1.08, particleSpeedSec: 7 },
  error: { pulseDurationSec: 1.8, pulseScale: 1.06, particleSpeedSec: 9 },
};

/** Helper: paleta + size em uma chamada. */
export function getSixyTheme(mood: SixyMood, size: SixySize) {
  return {
    palette: SIXY_PALETTES[mood],
    sizePx: SIXY_SIZE_PX[size],
    timing: SIXY_TIMINGS[mood],
  };
}
